import { arrayMove } from '@dnd-kit/sortable';

import { Item } from '@/features/backlog/types';

export interface Flat {
  item: Item;
  depth: number;
}

/** Flatten items into visual (depth-first) order by parent_id + position, honoring
 *  a set of collapsed parent ids. */
export function buildFlat(items: Item[], collapsed: Set<string>): Flat[] {
  const byParent = new Map<string, Item[]>();
  const ids = new Set(items.map((i) => i.id));
  for (const it of items) {
    const key = it.parent_id && ids.has(it.parent_id) ? it.parent_id : '__root__';
    const arr = byParent.get(key) ?? [];
    arr.push(it);
    byParent.set(key, arr);
  }
  for (const arr of byParent.values()) arr.sort((a, b) => a.position - b.position);
  const out: Flat[] = [];
  const walk = (parentKey: string, depth: number) => {
    for (const it of byParent.get(parentKey) ?? []) {
      out.push({ item: it, depth });
      const kids = byParent.get(it.id) ?? [];
      if (kids.length && !collapsed.has(it.id)) walk(it.id, depth + 1);
    }
  };
  walk('__root__', 0);
  return out;
}

export function childCount(items: Item[], id: string): number {
  return items.reduce((n, i) => (i.parent_id === id ? n + 1 : n), 0);
}

/**
 * The item a view should visually highlight for `selectedId`: the item itself when it's
 * visible, otherwise its nearest visible ancestor — the highest (closest-to-root) ancestor
 * that is collapsed, so `selectedId` is folded inside it and that ancestor is the on-screen
 * stand-in. `isCollapsed` reports a node's folded state in the view (for the Kanban, where
 * children are hidden by default, that's `!expanded`). Returns `selectedId` unchanged when it's
 * null, not on the board, or has no collapsed ancestor (i.e. it's already visible). Cycle-safe.
 */
export function visibleSelectedId(
  selectedId: string | null,
  byId: Map<string, Item>,
  isCollapsed: (id: string) => boolean,
): string | null {
  if (!selectedId || !byId.has(selectedId)) return selectedId;
  let foldAt: string | null = null;
  const seen = new Set<string>();
  let cur = byId.get(selectedId)!.parent_id;
  while (cur && byId.has(cur) && !seen.has(cur)) {
    seen.add(cur);
    if (isCollapsed(cur)) foldAt = cur; // keep climbing; the LAST hit is the highest collapsed ancestor
    cur = byId.get(cur)!.parent_id;
  }
  return foldAt ?? selectedId;
}

/** ids of an item and all its descendants. */
export function subtreeIds(items: Item[], id: string): Set<string> {
  const byParent = new Map<string, string[]>();
  for (const i of items) if (i.parent_id) (byParent.get(i.parent_id) ?? byParent.set(i.parent_id, []).get(i.parent_id)!).push(i.id);
  const out = new Set<string>([id]);
  const walk = (x: string) => (byParent.get(x) ?? []).forEach((k) => (out.has(k) ? null : (out.add(k), walk(k))));
  walk(id);
  return out;
}

export interface Projection {
  depth: number;
  parentId: string | null;
}

/** Given the flattened list (with the active item's subtree already removed),
 *  the active/over ids and the horizontal drag offset, compute where the item
 *  would land: its depth and new parent. Adapted from the dnd-kit sortable-tree
 *  example. */
export function getProjection(
  flat: Flat[],
  activeId: string,
  overId: string,
  dragOffsetX: number,
  indentWidth: number,
): Projection {
  const overIndex = flat.findIndex((f) => f.item.id === overId);
  const activeIndex = flat.findIndex((f) => f.item.id === activeId);
  if (overIndex === -1 || activeIndex === -1) return { depth: 0, parentId: null };

  const moved = arrayMove(flat, activeIndex, overIndex);
  const prev = moved[overIndex - 1];
  const next = moved[overIndex + 1];

  const dragDepth = Math.round(dragOffsetX / indentWidth);
  const projectedDepth = flat[activeIndex].depth + dragDepth;
  const maxDepth = prev ? prev.depth + 1 : 0;
  const minDepth = next ? next.depth : 0;
  const depth = Math.max(minDepth, Math.min(projectedDepth, maxDepth));

  let parentId: string | null = null;
  if (depth !== 0 && prev) {
    if (depth === prev.depth) parentId = prev.item.parent_id ?? null;
    else if (depth > prev.depth) parentId = prev.item.id;
    else {
      const ancestor = moved
        .slice(0, overIndex)
        .reverse()
        .find((f) => f.depth === depth);
      parentId = ancestor ? ancestor.item.parent_id ?? null : null;
    }
  }
  return { depth, parentId };
}
